import ImageWithSkeleton from "@/components/ui/ImageWithSkeleton";
import { useAchievementOperations } from "@/hooks/useAchievementOperations";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { IAchievement } from "@/types";
import { format } from "date-fns";
import { Building, Calendar } from "lucide-react";
import { useRouter } from "next/navigation";

interface AchievementCardProps {
    achievement: IAchievement;
    onClick: (achievement: IAchievement) => void;
    isAdmin?: boolean;
}

function AdminControls({ achievement }: { achievement: IAchievement }) {
    const { deleteAchievement } = useAchievementOperations();
    const router = useRouter();

    return (
        <div className="mt-3 flex justify-center gap-4">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    router.push(`/admin/achievements/${achievement._id}`);
                }}
                className="btn btn-pill-warning-outline flex-1"
            >
                Edit
            </button>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    deleteAchievement(achievement._id!);
                }}
                className="btn btn-pill-danger-outline flex-1"
            >
                Delete
            </button>
        </div>
    );
}

function AchievementCard({
    achievement,
    onClick,
    isAdmin,
}: AchievementCardProps) {
    const firstImgId = achievement.images?.[0]?._id;
    const { signedUrl, isLoading } = useSignedUrl(firstImgId);

    return (
        <div
            className="mx-auto flex w-full max-w-[400px] cursor-pointer flex-col justify-between gap-3 rounded-2xl border border-border-color p-4 transition duration-300 hover:scale-[1.02] hover:border-primary sm:mx-0"
            onClick={() => onClick(achievement)}
        >
            <ImageWithSkeleton
                src={signedUrl}
                alt={achievement.title}
                isLoadingUrl={isLoading}
                width={0}
                height={0}
                sizes="100vw"
                containerClassName="mb-2 aspect-[3/2] w-full rounded-xl"
                className="h-full w-full rounded-xl object-cover"
            />
            {/* Title */}
            <h3 className="text-center text-lg font-extrabold text-primary">
                {achievement.title}
            </h3>
            {/* Company & Date */}
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm">
                {/* Company */}
                <div className="flex items-center gap-1 rounded-full px-2 py-1">
                    <Building className="h-4 w-4" />
                    <span>{achievement.company}</span>
                </div>
                {/* Date */}
                <div className="flex items-center gap-1 rounded-full px-2 py-1">
                    <Calendar className="h-4 w-4" />
                    <span>
                        {format(new Date(achievement.date), "dd MMM yyyy")}
                    </span>
                </div>
            </div>
            {isAdmin && <AdminControls achievement={achievement} />}
        </div>
    );
}

export default AchievementCard;
